import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, LogIn, X, Loader2, CheckCircle2, Sparkles, User, BedDouble, Calendar, Phone } from 'lucide-react';

interface CheckInRow {
  id: string;
  roomNumber: string;
  guestName: string;
  phone: string;
  nights: number;
  checkIn: string;
  checkOut: string;
}

interface HotelRoom { id: number; roomNumber: string; }

interface VoiceCheckInHologramProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  rooms?: HotelRoom[];
  onCheckInCreated?: (reservation: any) => void;
}

export default function VoiceCheckInHologram({ isOpen, onClose, storeApiKey, rooms = [], onCheckInCreated }: VoiceCheckInHologramProps) {
  const [rows, setRows] = useState<CheckInRow[]>([]);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [status, setStatus] = useState<'listening' | 'saving' | 'saved' | 'idle'>('listening');

  // Auto-start Luna express check-in on open
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('luna-hotel-express-checkin-start'));
    }
  }, [isOpen]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setRows([]);
      setJustAdded(new Set());
      setSaving(false);
      setSaved(false);
      setSavedCount(0);
      setStatus('listening');
    }
  }, [isOpen]);

  // Listen for QUICK_CHECKIN actions from Luna — accumulate into grid
  useEffect(() => {
    const handler = (e: Event) => {
      const { action, roomNumber, guestName, nights: rawNights, phone } = (e as CustomEvent).detail || {};
      if (action !== 'QUICK_CHECKIN' || !roomNumber) return;
      const id = `${roomNumber}_${Date.now()}`;
      const checkIn = new Date().toISOString().slice(0, 10);
      const nights = parseInt(rawNights) || 1;
      const checkOutDate = new Date(checkIn);
      checkOutDate.setDate(checkOutDate.getDate() + nights);
      const checkOut = checkOutDate.toISOString().slice(0, 10);
      const row: CheckInRow = { id, roomNumber: String(roomNumber), guestName: guestName || '', phone: phone || '', nights, checkIn, checkOut };
      setRows(prev => {
        const exists = prev.findIndex(r => r.roomNumber === String(roomNumber));
        if (exists >= 0) { const next = [...prev]; next[exists] = row; return next; }
        return [...prev, row];
      });
      setJustAdded(prev => { const n = new Set(prev); n.add(id); return n; });
      setTimeout(() => setJustAdded(prev => { const n = new Set(prev); n.delete(id); return n; }), 1500);
    };
    window.addEventListener('luna-hotel-action', handler);
    return () => window.removeEventListener('luna-hotel-action', handler);
  }, []);

  // Listen for luna save command
  useEffect(() => {
    const handler = async () => { await doSaveAll(); };
    window.addEventListener('luna-checkin-grid-save', handler);
    return () => window.removeEventListener('luna-checkin-grid-save', handler);
  }, [rows, storeApiKey, rooms]);

  const doSaveAll = async () => {
    if (rows.length === 0) return;
    setSaving(true);
    setStatus('saving');
    const token = localStorage.getItem('kangavena:cashierToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let count = 0;
    try {
      for (const row of rows) {
        const room = rooms.find(r => String(r.roomNumber) === String(row.roomNumber));
        if (!room) continue;
        const guestNote = [row.guestName, row.phone].filter(Boolean).join(' | ');
        const res = await fetch('/api/hotel/reservations', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            roomId: room.id,
            checkInDate: row.checkIn,
            checkOutDate: row.checkOut,
            adults: 1,
            children: 0,
            paymentMethod: 'cash',
            source: 'WALK_IN',
            specialRequests: row.guestName ? `Guest: ${row.guestName}${row.phone ? ', Phone: ' + row.phone : ''}` : null,
            notes: `Luna Voice Check-In${guestNote ? ': ' + guestNote : ''}`,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          onCheckInCreated?.(data);
          count++;
        }
      }
      setSaved(true);
      setSavedCount(count);
      setStatus('saved');
      setTimeout(() => { onClose(); }, 2500);
    } catch { /* silent */ } finally { setSaving(false); }
  };

  const today = new Date().toISOString().slice(0, 10);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="voice-checkin-hologram"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-xl flex items-end md:items-center justify-center p-0 md:p-6"
      >
        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 80, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full max-w-2xl bg-[#060d18] border border-cyan-500/20 rounded-t-3xl md:rounded-3xl overflow-hidden shadow-[0_0_80px_rgba(6,182,212,0.15)]"
        >
          {/* Glow top bar */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                <LogIn size={17} className="text-cyan-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-cyan-400/60 uppercase tracking-widest">Luna AI</p>
                <p className="text-sm font-bold text-white">Express Check-In</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 transition-all">
              <X size={14} />
            </button>
          </div>

          {/* Status strip */}
          <div className="px-5 py-3 flex items-center gap-3 bg-cyan-500/5 border-b border-cyan-500/10">
            {status === 'listening' && (
              <>
                <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-cyan-400" />
                <p className="text-[11px] text-cyan-300 font-mono">
                  Say: <span className="text-white font-bold">"Room 101, John Doe, 3 nights"</span> — Luna fills the form instantly
                </p>
              </>
            )}
            {status === 'saving' && (
              <><Loader2 size={13} className="text-cyan-400 animate-spin" /><p className="text-[11px] text-cyan-300 font-mono">Creating reservations…</p></>
            )}
            {status === 'saved' && (
              <><CheckCircle2 size={13} className="text-emerald-400" /><p className="text-[11px] text-emerald-300 font-mono">✓ {savedCount} check-in{savedCount !== 1 ? 's' : ''} created!</p></>
            )}
          </div>

          {/* Grid */}
          <div className="px-5 py-4 min-h-[180px] max-h-[380px] overflow-y-auto">
            {rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-4">
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2, repeat: Infinity }}
                  className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Mic size={28} className="text-cyan-400" />
                </motion.div>
                <p className="text-slate-500 text-sm text-center">
                  Speak a check-in and Luna will populate it here
                </p>
                <p className="text-slate-600 text-[11px] font-mono text-center">"Room 205, Maria Silva, 2 nights" or<br/>"Check in John, room 310, 4 nights, +94771234567"</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {rows.map(row => (
                  <motion.div key={row.id}
                    initial={justAdded.has(row.id) ? { scale: 0.95, opacity: 0, x: -10 } : false}
                    animate={{ scale: 1, opacity: 1, x: 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className={`grid grid-cols-2 md:grid-cols-4 gap-3 p-3.5 rounded-xl border transition-all ${
                      justAdded.has(row.id)
                        ? 'bg-cyan-500/15 border-cyan-400/50 shadow-[0_0_12px_rgba(6,182,212,0.2)]'
                        : 'bg-white/4 border-white/8'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <BedDouble size={12} className="text-cyan-400 shrink-0" />
                      <div>
                        <p className="text-[9px] font-mono text-slate-500 uppercase">Room</p>
                        <p className="text-sm font-bold text-cyan-400">{row.roomNumber || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={12} className="text-fuchsia-400 shrink-0" />
                      <div>
                        <p className="text-[9px] font-mono text-slate-500 uppercase">Guest</p>
                        <p className="text-sm font-semibold text-white truncate">{row.guestName || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-[9px] font-mono text-slate-500 uppercase">Nights</p>
                        <p className="text-sm font-semibold text-emerald-400">{row.nights}n — {row.checkIn}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={12} className="text-yellow-400 shrink-0" />
                      <div>
                        <p className="text-[9px] font-mono text-slate-500 uppercase">Phone</p>
                        <p className="text-sm font-semibold text-yellow-400">{row.phone || '—'}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          {rows.length > 0 && !saved && (
            <div className="px-5 pb-5 pt-2 border-t border-white/8 flex gap-3">
              <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-white/10 text-slate-400 text-sm font-semibold hover:bg-white/5 hover:text-white transition-all">
                Cancel
              </button>
              <motion.button whileTap={{ scale: 0.97 }}
                onClick={doSaveAll}
                disabled={saving}
                className="flex-2 flex-1 h-11 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-black shadow-[0_4px_14px_rgba(6,182,212,0.35)] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm {rows.length} Check-In{rows.length !== 1 ? 's' : ''}
              </motion.button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
